// UI-only drawer with an outbox letter's details. Data comes from the page
// (useAdminOutboxDetail); no fetching here. The letter body (bodyText) is
// deliberately NOT shown in P2 — minimal PII (plan §7.3, Out of scope).
import type { ReactElement } from 'react';
import { Descriptions, Drawer, Empty, Skeleton, Typography } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { formatDateTime } from '../lib/format.js';
import type { AdminOutboxDetail } from '../api/admin.client.js';

import { OutboxStatusBadge } from './OutboxStatusBadge.js';

const { Text } = Typography;

export interface AdminMailDrawerProps {
  open: boolean;
  messageId: string | null;
  detail: AdminOutboxDetail | undefined;
  isLoading: boolean;
  onClose: () => void;
}

export function AdminMailDrawer({
  open,
  messageId,
  detail,
  isLoading,
  onClose,
}: AdminMailDrawerProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      title={detail ? detail.subject : t('crm.admin.mail.detail_title')}
      destroyOnHidden
    >
      {isLoading || (detail === undefined && messageId !== null) ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : detail === undefined ? (
        <Empty description={t('crm.admin.mail.detail_empty')} />
      ) : (
        <div className="flex flex-col gap-6">
          <Descriptions
            size="small"
            column={1}
            bordered
            items={[
              {
                key: 'to',
                label: t('crm.admin.mail.detail_to'),
                children: (
                  <Text className="font-mono" style={{ fontSize: 12 }}>
                    {detail.toEmail}
                    {detail.toName !== null ? ` · ${detail.toName}` : ''}
                  </Text>
                ),
              },
              {
                key: 'workspace',
                label: t('crm.admin.mail.detail_workspace'),
                children: (
                  <Text style={{ fontSize: 12 }}>
                    {detail.workspace.name}{' '}
                    <Text className="font-mono" type="secondary" style={{ fontSize: 11 }}>
                      {detail.workspace.slug}
                    </Text>
                  </Text>
                ),
              },
              {
                key: 'kind',
                label: t('crm.admin.mail.detail_kind'),
                children: t(`crm.admin.mail.kind.${detail.kind}`),
              },
              {
                key: 'status',
                label: t('crm.admin.mail.detail_status'),
                children: <OutboxStatusBadge status={detail.status} />,
              },
              {
                key: 'attempts',
                label: t('crm.admin.mail.detail_attempts'),
                children: (
                  <Text className="font-mono" style={{ fontSize: 12 }}>
                    {detail.attempts}
                  </Text>
                ),
              },
              {
                key: 'availableAt',
                label: t('crm.admin.mail.detail_available_at'),
                children: formatDateTime(detail.availableAt),
              },
              {
                key: 'lastError',
                label: t('crm.admin.mail.detail_last_error'),
                children:
                  detail.lastError === null ? (
                    <Text disabled style={{ fontSize: 12 }}>
                      —
                    </Text>
                  ) : (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {detail.lastError}
                    </Text>
                  ),
              },
              {
                key: 'sentAt',
                label: t('crm.admin.mail.detail_sent_at'),
                children: formatDateTime(detail.sentAt),
              },
              {
                key: 'deliveredAt',
                label: t('crm.admin.mail.detail_delivered_at'),
                children: formatDateTime(detail.deliveredAt),
              },
              {
                key: 'providerMessageId',
                label: t('crm.admin.mail.detail_provider_id'),
                children: (
                  <Text className="font-mono" style={{ fontSize: 12 }}>
                    {detail.providerMessageId ?? '—'}
                  </Text>
                ),
              },
              {
                key: 'dedupeKey',
                label: t('crm.admin.mail.detail_dedupe_key'),
                children: (
                  <Text className="font-mono" style={{ fontSize: 11 }}>
                    {detail.dedupeKey}
                  </Text>
                ),
              },
              {
                key: 'createdAt',
                label: t('crm.admin.mail.detail_created_at'),
                children: formatDateTime(detail.createdAt),
              },
              {
                key: 'updatedAt',
                label: t('crm.admin.mail.detail_updated_at'),
                children: formatDateTime(detail.updatedAt),
              },
            ]}
          />
        </div>
      )}
    </Drawer>
  );
}
